import * as React from "react";
import styled from "@emotion/styled";
import { UseFieldArrayMove, UseFieldArrayRemove } from "react-hook-form";
import { Button } from "./Button";
import { IconClose, IconChevron } from "./icons";
import { ScreenReaderText } from "./ScreenReaderText";

type RepeaterFieldsProps = {
  children: React.ReactNode;
  handleAppend: () => void;
  appendLabel: string;
};

function RepeaterFields({
  children,
  appendLabel,
  handleAppend,
  ...props
}: RepeaterFieldsProps) {
  return (
    <>
      <StyledRepeaterFields {...props}>{children}</StyledRepeaterFields>

      <AppendButton onClick={handleAppend} title={appendLabel} size="sm">
        {appendLabel}
      </AppendButton>
    </>
  );
}

type RepeaterFieldProps = {
  children: React.ReactNode;
  handleDelete: () => void;
  handleMoveUp?: () => void;
  handleMoveDown?: () => void;
};

function RepeaterField({
  children,
  handleDelete,
  handleMoveUp,
  handleMoveDown,
  ...props
}: RepeaterFieldProps) {
  return (
    <StyledRepeaterField {...props}>
      <Actions>
        <MoveButton
          dir="up"
          onClick={handleMoveUp}
          disabled={!handleMoveUp}
          title="Move up"
        >
          <ScreenReaderText>Move item up one row</ScreenReaderText>
          <IconChevron aria-label="hidden" />
        </MoveButton>

        <MoveButton
          dir="down"
          onClick={handleMoveDown}
          disabled={!handleMoveDown}
          title="Move down"
        >
          <ScreenReaderText>Move item down one row</ScreenReaderText>
          <IconChevron aria-label="hidden" />
        </MoveButton>

        <DeleteButton onClick={handleDelete} title="Delete row">
          <ScreenReaderText>Delete row</ScreenReaderText>
          <IconClose aria-label="hidden" />
        </DeleteButton>
      </Actions>

      <Inputs>{children}</Inputs>
    </StyledRepeaterField>
  );
}

/**
 * Create event handlers for each RepeaterField's ActionButtons
 *
 * @param index - Current index of fields[] element
 * @param length - useFieldArray's fields.length
 * @param move - useFieldArray's move function
 * @param remove - useFieldArray's remove function
 */
RepeaterField.createHandlers = (
  index: number,
  length: number,
  move: UseFieldArrayMove,
  remove: UseFieldArrayRemove
) => ({
  handleDelete: () => remove(index),
  handleMoveUp: index > 0 ? () => move(index, index - 1) : undefined,
  handleMoveDown: index < length - 1 ? () => move(index, index + 1) : undefined,
});

const StyledRepeaterFields = styled.ul({
  listStyleType: "none",
  padding: 0,
  margin: 0,
});

const AppendButton = styled(Button)({
  marginTop: "1rem",
});
AppendButton.defaultProps = {
  variant: "default",
};

const StyledRepeaterField = styled.li(({ theme }) => ({
  border: `1px solid ${theme.colors.gray60}`,
  display: "flex",
  flexDirection: "row",
  "& + &": {
    marginTop: -1,
  },
}));

const Actions = styled.div(({ theme }) => ({
  background: theme.colors.gray10,
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  padding: "0.5rem",
}));

const ActionButton = styled.button(({ theme }) => ({
  alignItems: "center",
  borderRadius: theme.borderRadius.round,
  border: `1px solid`,
  display: "flex",
  height: 30,
  justifyContent: "center",
  margin: 0,
  padding: 0,
  width: 30,
  "&[disabled]": {
    opacity: ".2",
  },
  svg: {
    height: "auto",
    width: 14,
  },
}));
ActionButton.defaultProps = { type: "button" };

const MoveButton = styled(ActionButton, {
  shouldForwardProp: (prop) => prop !== "dir",
})<{ dir: "up" | "down" }>(({ dir, theme }) => ({
  borderColor: theme.colors.black,
  color: theme.colors.black,
  cursor: "pointer",
  svg: {
    marginBottom: dir === "down" ? -2 : 2,
    transform: dir === "down" ? "rotate(180deg)" : undefined,
  },
}));

const DeleteButton = styled(ActionButton)(({ theme }) => ({
  borderColor: theme.colors.red,
  color: theme.colors.red,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  marginTop: 10,
}));

const Inputs = styled.div({
  flex: 1,
  display: "flex",
  gap: 20,
  flexDirection: "column",
  padding: "1rem",
});

export { RepeaterFields, RepeaterField };
